import React, { useEffect } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { useDispatch } from "react-redux";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  Department,
  fetchDepartments,
  updateDepartment,
} from "src/features/departmentsSlice";
import { AppDispatch } from "src/store";
import { useTranslation } from "react-i18next";

interface EditDepartmentModalProps {
  open: boolean;
  onClose: () => void;
  department: Department | null;
}

interface DepartmentFormInputs {
  name: string;
  location?: string;
  team?: string;
  industry?: string;
}

const EditDepartmentModal: React.FC<EditDepartmentModalProps> = ({
  open,
  onClose,
  department,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<DepartmentFormInputs>({
    defaultValues: {
      name: "",
      location: "",
      team: "",
      industry: "",
    },
  });

  useEffect(() => {
    if (!department) return;
    reset({
      name: department.name || "",
      location: department.location || "",
      team: department.team || "",
      industry: department.industry || "",
    });
  }, [department, reset]);

  const onSubmit: SubmitHandler<DepartmentFormInputs> = (data) => {
    if (!department) return;
    dispatch(updateDepartment({ departmentId: department.id, departmentData: data })).then((action) => {
      if (updateDepartment.fulfilled.match(action)) {
        dispatch(fetchDepartments());
        onClose();
      }
    });
  };

  if (!department) return null;

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        {t("departments.edit.title")}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" gutterBottom>
          {t("departments.edit.description")}
        </Typography>
        <form onSubmit={handleSubmit(onSubmit)}>
          <TextField
            fullWidth
            label={t("departments.create.name")}
            {...register("name", { required: t("departments.create.nameRequired") })}
            error={!!errors.name}
            helperText={errors.name ? errors.name.message : null}
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            label={t("departments.create.location")}
            {...register("location")}
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            label={t("departments.create.team")}
            {...register("team")}
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            label={t("departments.create.industry")}
            {...register("industry")}
            sx={{ mt: 2 }}
          />
          <DialogActions>
            <Button onClick={onClose}>{t("departments.create.cancel")}</Button>
            <Button type="submit" variant="contained">
              {t("departments.edit.saveChanges")}
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditDepartmentModal;
